/**
 * Dev-sync J1 — Plotly layout + config for the psychrometric chart.
 * Direct port of legacy buildLayout (psychrometric_chart.js:1541-1589) and
 * the renderChart config literal (js:681-695).
 */

import type { PsyAxes, PsyChartData } from './psychrometricApi';
import { COLORS, DEFAULT_AXES, toGrains } from './chartTraces';

type WetBulbLabel = { t_wb: number; t_db: number; w: number };

/**
 * The wet-bulb scale, as Plotly layout annotations rather than a trace:
 * the numbers must not join the scatter traces, whose indices
 * declutterLabels maps 1:1 onto DOM nodes, and which the click-to-add-a-
 * state-point hit test walks.
 *
 * Each number is anchored where its own wet-bulb line meets the saturation
 * curve (the backend sends that point — saturated air reads the same dry
 * bulb and wet bulb), then pushed up and to the LEFT so it lands in the
 * empty area outside the curve instead of on top of it. That area is the
 * left edge of the chart, which is exactly where a paper psychrometric
 * chart carries this scale.
 */
export function wetBulbAnnotations(
  labels: WetBulbLabel[] | undefined,
  axes: PsyAxes
): Record<string, unknown>[] {
  if (!labels || !labels.length) return [];
  const span = axes.t_db_max - axes.t_db_min || 1;
  const out: Record<string, unknown>[] = labels.map((label) => {
    // Up-and-left of the anchor: the saturation curve rises to the right,
    // so that is the side with nothing on it at every point of the curve.
    // The exception is a label sitting against the left edge — the coldest
    // one always does — where "left" is off the plot and the number gets
    // cut in half by the margin. There the curve is nearly flat, so the
    // area up and to the RIGHT is just as empty; put it there instead.
    const atLeftEdge = (label.t_db - axes.t_db_min) / span < 0.04;
    return {
      x: label.t_db,
      y: toGrains(label.w),
      text: label.t_wb.toFixed(2),
      showarrow: false,
      xanchor: atLeftEdge ? 'left' : 'right',
      yanchor: 'bottom',
      xshift: atLeftEdge ? 3 : -3,
      yshift: 3,
      font: { size: 9, color: COLORS.saturation },
    };
  });
  // One caption so the numbers are not mistaken for a second dry-bulb
  // scale. Placed against the top-left of the plot area, the only part of
  // the chart the saturation curve never reaches.
  out.push({
    xref: 'paper',
    yref: 'paper',
    x: 0.02,
    y: 0.97,
    text: 'Wet-bulb temperature (°F)',
    showarrow: false,
    xanchor: 'left',
    yanchor: 'top',
    font: { size: 10, color: COLORS.saturation },
  });
  return out;
}

export function buildLayout(
  axes: PsyAxes | null | undefined,
  curves?: PsyChartData['curves'] | null
): Record<string, unknown> {
  const a = axes || DEFAULT_AXES;
  return {
    annotations: wetBulbAnnotations(curves?.wet_bulb_labels, a),
    // l trimmed to just enough room for the leftmost x-tick label not to
    // clip (the y-axis is on the RIGHT); t trimmed 5px; r/b grown for the
    // tick-label standoff + axis-title breathing room (js:1544-1551).
    margin: { l: 24, r: 110, t: 25, b: 70 },
    xaxis: {
      title: 'Dry-bulb temperature (°F)',
      range: [a.t_db_min, a.t_db_max],
      gridcolor: '#eaeaea',
      zeroline: false,
      tickformat: '.2f',
      // Invisible outside ticks as a spacer: transparent tickcolor, but
      // ticklen still pushes the labels 5px off the axis line (js:1559-1564).
      ticks: 'outside',
      ticklen: 5,
      tickcolor: 'rgba(0,0,0,0)',
    },
    yaxis: {
      title: { text: 'Humidity ratio (gr/lb dry air)', standoff: 25 },
      range: [toGrains(a.w_min), toGrains(a.w_max)],
      side: 'right',
      gridcolor: '#eaeaea',
      zeroline: false,
      tickformat: '.2f',
      ticks: 'outside',
      ticklen: 5,
      tickcolor: 'rgba(0,0,0,0)',
    },
    showlegend: false,
    hovermode: 'closest',
    plot_bgcolor: '#fafafa',
    // Adding a state point (J2) is the only mouse interaction the chart
    // supports — no drag-zoom/pan/box-select; also sidesteps Plotly's
    // click-with-incidental-movement rubber-band-zoom bug (js:1580-1587).
    dragmode: false,
  };
}

/** renderChart's config (js:681-695): doubleClick off (fights the J2
 * dblclick-to-edit and shifts axis ranges under its hit-test); no mode bar,
 * no scroll zoom; PNG export lives in the dialog header (J3a), not Plotly's
 * toolbar. */
export const PLOT_CONFIG = {
  responsive: true,
  displaylogo: false,
  displayModeBar: false,
  scrollZoom: false,
  doubleClick: false,
} as const;
